import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "../lib/api";
import { AppShell, PageHeader } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Check } from "lucide-react";
import {
  ArrangementStep, CalculationStep, SettingsStep, agreementPayload, defaultAgreementForm,
  ENGAGEMENTS, METHODS, WORK_PATTERNS,
} from "../components/AgreementFields";

const DAYS = [
  { v: "mon", l: "Mon" }, { v: "tue", l: "Tue" }, { v: "wed", l: "Wed" },
  { v: "thu", l: "Thu" }, { v: "fri", l: "Fri" }, { v: "sat", l: "Sat" }, { v: "sun", l: "Sun" },
];

const STEPS = ["Practice", "Arrangement", "Calculation", "Settings"];

export default function AddPractice() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [practice, setPractice] = useState({
    name: "", suburb: "", contact_person: "", working_days: [], work_pattern: "",
    agreement_start: "2026-07-01", agreement_end: "",
  });
  const [agr, setAgr] = useState(defaultAgreementForm("2026-07-01"));
  const setP = (k, v) => setPractice((f) => ({ ...f, [k]: v }));
  const setA = (k, v) => setAgr((f) => ({ ...f, [k]: v }));

  const toggleDay = (d) =>
    setP("working_days", practice.working_days.includes(d)
      ? practice.working_days.filter((x) => x !== d)
      : [...practice.working_days, d]);

  const next = () => {
    if (step === 0 && !practice.name.trim()) return toast.error("Please enter a practice name.");
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/practices", {
        name: practice.name.trim(),
        suburb: practice.suburb || null,
        contact_person: practice.contact_person || null,
        working_days: practice.working_days,
        work_pattern: practice.work_pattern || null,
        agreement_start: practice.agreement_start,
        agreement_end: practice.agreement_end || null,
        agreement: { ...agreementPayload(agr), effective_start: practice.agreement_start },
      });
      qc.invalidateQueries();
      toast.success("Practice added");
      navigate("/practices");
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  return (
    <AppShell>
      <PageHeader title="Add Practice" subtitle={`Step ${step + 1} of ${STEPS.length} — ${STEPS[step]}`} onBack={() => (step === 0 ? navigate(-1) : setStep(step - 1))} />

      <div className="px-5 mb-4 flex gap-1.5">
        {STEPS.map((_, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-[#0D786A]" : "bg-slate-200"}`} />
        ))}
      </div>

      <div className="px-5 pb-40 df-fade-in">
        <div className="df-card p-5">
          {step === 0 && (
            <div className="space-y-4">
              <div>
                <Label className="text-slate-700">Practice name</Label>
                <Input data-testid="practice-name" value={practice.name} onChange={(e) => setP("name", e.target.value)}
                  className="mt-1.5 h-12 rounded-xl bg-white" placeholder="e.g. Riverside Dental" />
              </div>
              <div>
                <Label className="text-slate-700">Location / suburb</Label>
                <Input data-testid="practice-suburb" value={practice.suburb} onChange={(e) => setP("suburb", e.target.value)}
                  className="mt-1.5 h-12 rounded-xl bg-white" placeholder="e.g. Toowong" />
              </div>
              <div>
                <Label className="text-slate-700">Contact person (optional)</Label>
                <Input data-testid="practice-contact" value={practice.contact_person} onChange={(e) => setP("contact_person", e.target.value)}
                  className="mt-1.5 h-12 rounded-xl bg-white" placeholder="Practice manager" />
              </div>
              <div>
                <Label className="text-slate-700">Work pattern (optional)</Label>
                <p className="text-xs text-slate-400 mt-0.5">For your planning only — not required to use Molaroo.</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {WORK_PATTERNS.map((w) => (
                    <button key={w.v} type="button" data-testid={`pattern-${w.v}`}
                      onClick={() => setP("work_pattern", practice.work_pattern === w.v ? "" : w.v)}
                      className={`px-3.5 h-10 rounded-xl border text-sm font-medium df-pressable ${
                        practice.work_pattern === w.v
                          ? "bg-[#0D786A] text-white border-[#0D786A]"
                          : "bg-white text-slate-600 border-slate-200"}`}>
                      {w.l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-slate-700">Usual working days (optional)</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {DAYS.map((d) => (
                    <button key={d.v} type="button" data-testid={`day-${d.v}`} onClick={() => toggleDay(d.v)}
                      className={`px-3.5 h-10 rounded-xl border text-sm font-medium df-pressable ${
                        practice.working_days.includes(d.v)
                          ? "bg-[#0D786A] text-white border-[#0D786A]"
                          : "bg-white text-slate-600 border-slate-200"}`}>
                      {d.l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-700">Agreement start</Label>
                  <Input type="date" data-testid="practice-start" value={practice.agreement_start}
                    onChange={(e) => setP("agreement_start", e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white" />
                </div>
                <div>
                  <Label className="text-slate-700">End (optional)</Label>
                  <Input type="date" data-testid="practice-end" value={practice.agreement_end}
                    onChange={(e) => setP("agreement_end", e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white" />
                </div>
              </div>
            </div>
          )}
          {step === 1 && <ArrangementStep form={agr} set={setA} />}
          {step === 2 && <CalculationStep form={agr} set={setA} />}
          {step === 3 && (
            <div className="space-y-4">
              <SettingsStep form={{ ...agr, effective_start: practice.agreement_start }} set={setA} />
              <div className="rounded-xl bg-[#F4FBF9] border border-emerald-100 p-4 text-sm">
                <div className="font-semibold text-slate-900 mb-1">{practice.name || "New practice"}</div>
                <div className="text-slate-600">{ENGAGEMENTS.find((e) => e.v === agr.engagement_type)?.l}</div>
                <div className="text-slate-600">{METHODS.find((e) => e.v === agr.calc_method)?.l}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="df-actionbar">
        {step < STEPS.length - 1 ? (
          <Button data-testid="wizard-next" onClick={next} className="flex-1 h-12 rounded-xl bg-[#0D786A] hover:bg-[#0A6055] df-pressable">
            Continue
          </Button>
        ) : (
          <Button data-testid="wizard-save" onClick={save} disabled={saving}
            className="flex-1 h-12 rounded-xl bg-[#0D786A] hover:bg-[#0A6055] df-pressable">
            <Check size={18} className="mr-1" /> {saving ? "Saving…" : "Save Practice"}
          </Button>
        )}
      </div>
    </AppShell>
  );
}
